import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { HistoryOutlined } from '@ant-design/icons';
import WorkOrderRowDisplay from '../work_order_row_display/WorkOrderRowDisplay';
import { SimpleRecordContainer } from '../simple_record_container/SimpleRecordContainer';
import { SeeAllButton } from '../see_all_button/SeeAllButton';
import { withUserType } from '../withUserType';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getWorkOrderArchivalKey,
	OPEN_WORK_ORDER_DISPLAY_STATUSES,
} from '../../utils/WorkOrderUtils';
import { WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import { searchWorkOrdersWithCount } from '../../thunks/work_orders_thunks';

const PREVIEW_COUNT = 3;

// Everything short of Completed/Cancelled — the same set the "See All" list is filtered to, so
// the header count and the destination agree.
const INCOMPLETE_DISPLAY_STATUSES = [
	...OPEN_WORK_ORDER_DISPLAY_STATUSES,
	WORK_ORDER_DISPLAY_STATUSES.deferred,
];

interface EarlierWorkOrdersResult {
	workOrders: any[];
	count: number;
}

interface WalkthroughTaskEarlierWorkOrdersProps {
	walkthrough: any;
	taskAnswer: any;
	getWorkOrderLink: (workOrder: any) => string;
	userType?: string;
	currentUser?: any;
	searchWorkOrders?: (
		userType: string,
		filters: object,
		limit: number
	) => Promise<EarlierWorkOrdersResult>;
}

/**
 * Incomplete work orders raised from this same task on earlier walkthroughs of the location.
 * A task answer carries two keys: `siteSurveyTaskHash` is shared by the same task on every
 * walkthrough, while `titleId` (a work order's `siteSurveyTaskTitleId`) scopes to this walkthrough
 * only. So "earlier" is everything matching the hash minus what matches the titleId — the latter
 * being the task's own work orders, listed directly above.
 */
const WalkthroughTaskEarlierWorkOrders: FC<WalkthroughTaskEarlierWorkOrdersProps> = ({
	walkthrough,
	taskAnswer,
	getWorkOrderLink,
	userType,
	currentUser,
	searchWorkOrders,
}) => {
	const history = useHistory();
	const [result, setResult] = useState<EarlierWorkOrdersResult | null>(null);

	const titleId = nullSafeGet('titleId', taskAnswer);
	const taskHash = nullSafeGet('siteSurveyTaskHash', taskAnswer);
	const locationId = nullSafeGet('locationId', walkthrough);
	const archivalKey = getWorkOrderArchivalKey(currentUser);
	// Re-fetch when a link/unlink changes the task's own work orders — those move in or out of the
	// "this walkthrough" side of the subtraction.
	const linkedCount = nullSafeGetOrElse('workOrders', taskAnswer, []).length;
	const linkedIdsKey = nullSafeGetOrElse('workOrders', taskAnswer, [])
		.map((workOrder) => nullSafeGet('id', workOrder))
		.join(',');

	useEffect(() => {
		if (!taskHash || !titleId || !locationId) {
			setResult(null);
			return undefined;
		}
		let cancelled = false;
		const incompleteFilters = {
			locationIds: [locationId],
			displayStatuses: INCOMPLETE_DISPLAY_STATUSES,
			[archivalKey]: false,
		};
		// The search endpoint can't exclude a titleId, so over-fetch the across-walkthroughs page by
		// the task's own work orders (enough to fill the preview however many of them sort first),
		// and take the exact size of the subtraction from the server.
		Promise.all([
			searchWorkOrders(
				userType,
				{ ...incompleteFilters, siteSurveyTaskHash: taskHash },
				linkedCount + PREVIEW_COUNT
			),
			searchWorkOrders(userType, { ...incompleteFilters, siteSurveyTaskTitleId: titleId }, 1),
		])
			.then(([acrossWalkthroughs, thisWalkthrough]) => {
				if (cancelled) return;
				setResult({
					workOrders: acrossWalkthroughs.workOrders
						.filter((workOrder) => workOrder.siteSurveyTaskTitleId !== titleId)
						.slice(0, PREVIEW_COUNT),
					count: acrossWalkthroughs.count - thisWalkthrough.count,
				});
			})
			// This section only supplements the task's own work orders; on failure it stays hidden
			// rather than replacing the task view with an error.
			.catch(() => !cancelled && setResult(null));
		return () => {
			cancelled = true;
		};
	}, [
		searchWorkOrders,
		userType,
		taskHash,
		titleId,
		locationId,
		archivalKey,
		linkedCount,
		linkedIdsKey,
	]);

	if (!result || result.count <= 0 || result.workOrders.length === 0) {
		return null;
	}

	const seeAllParams = new URLSearchParams({
		siteSurveyTaskHash: taskHash,
		// Leave out this walkthrough's own work orders, as the card above does.
		notSiteSurveyTaskTitleId: titleId,
		locationIds: `${locationId}`,
		displayStatuses: INCOMPLETE_DISPLAY_STATUSES.join(','),
	}).toString();
	const hasMore = result.count > result.workOrders.length;

	return (
		<div className="mt-4 overflow-hidden rounded border border-solid border-calendulaGold-5 bg-white">
			<div className="flex items-center gap-3 border-0 border-b border-solid border-calendulaGold-5 bg-calendulaGold-1 px-6 py-3">
				<HistoryOutlined className="text-sunsetOrange-8" />
				<span className="bodybold text-character-primary">
					{result.count} Incomplete work order{result.count === 1 ? '' : 's'} from earlier
					walkthroughs
				</span>
			</div>
			<div className="relative">
				<SimpleRecordContainer
					items={result.workOrders.map((workOrder) => (
						<Link
							key={workOrder.id}
							to={getWorkOrderLink(workOrder)}
							className="flex w-full bg-white px-4 py-6"
						>
							{/* Same flags as the link modal's picker rows: no hover-revealed note preview or
							    add-label, which grow the row on hover. */}
							<WorkOrderRowDisplay
								workOrder={workOrder}
								simpler={false}
								hideNotePreview
								hideAddLabel
							/>
						</Link>
					))}
				/>
				{hasMore ? (
					<div className="pointer-events-none absolute bottom-0 left-0 h-16 w-full bg-gradient-to-b from-transparent to-white" />
				) : null}
			</div>
			{hasMore ? (
				<SeeAllButton
					onClick={() => history.push(`/${userType}/workOrders/overview/all?${seeAllParams}`)}
				/>
			) : null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

// No ownProps here: connect would then rebuild searchWorkOrders on every parent render, and the
// fetch effect depends on it.
const mapDispatchToProps = () => ({
	searchWorkOrders: (userType, filters, limit) =>
		searchWorkOrdersWithCount(userType)(filters, limit),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(WalkthroughTaskEarlierWorkOrders as any) as any
);
